import { useEffect, useRef, useState } from "react";
import "./GroupChat.css";
import { getGroupMessages } from "../../services/groupService";
import CopyButton from "../textCopyButton/CopyButton";
import { parseMessage } from "../../utils/parseMessage";

const GroupChat = ({ user, group, socket }) => {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef(null);
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeouts = useRef({});
  const chatContainerRef = useRef(null);
  const hasAutoScrolled = useRef(false);

  // ============================
  // Load previous messages
  // ============================
  useEffect(() => {
    if (!group?._id) return;

    const fetchMessages = async () => {
      try {
        const res = await getGroupMessages(group._id);
        setMessages(res.data || []);
      } catch (err) {
        console.error("Failed to load group messages:", err);
      }
    };

    fetchMessages();
  }, [group]);

  // ============================
  // Join socket room and listen for messages
  // ============================
  useEffect(() => {
    if (!socket || !group?._id) return;

    socket.emit("group:join", group._id);

    socket.on("group:messages:load", (msgs) => {
      setMessages(msgs || []);
    });

    const handleNewMessage = (msg) => {
      if (msg.chat === group._id) {
        setMessages((prev) => [...prev, msg]);
      }
    };
    socket.on("group:message:new", handleNewMessage);

    return () => {
      socket.off("group:message:new", handleNewMessage);
      socket.off("group:messages:load");
    };
  }, [socket, group]);

  // ============================
  // Auto scroll to bottom
  // ============================
  const isUserNearBottom = () => {
    const el = chatContainerRef.current;
    if (!el) return true;

    const threshold = 120; // px
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  };

  useEffect(() => {
    if (!messages.length) return;

    // Force scroll on first load
    if (!hasAutoScrolled.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      hasAutoScrolled.current = true;
      return;
    }

    // Conditional scroll for new messages
    if (isUserNearBottom()) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    hasAutoScrolled.current = false;
  }, [group?._id]);

  // ============================
  // Typing indicator logic
  // ============================
  useEffect(() => {
    if (!socket || !group?._id) return;

    const handleTypingEvent = ({ groupId, userId, username, isTyping }) => {
      if (groupId !== group._id || userId === user._id) return;

      setTypingUsers((prev) => {
        const updated = { ...prev };
        if (isTyping) {
          updated[userId] = username;
        } else {
          delete updated[userId];
        }
        return updated;
      });

      clearTimeout(typingTimeouts.current[userId]);
      typingTimeouts.current[userId] = setTimeout(() => {
        setTypingUsers((prev) => {
          const updated = { ...prev };
          delete updated[userId];
          return updated;
        });
      }, 3000);
    };

    socket.on("group:typing", handleTypingEvent);

    return () => {
      socket.off("group:typing", handleTypingEvent);
    };
  }, [socket, group?._id, user._id]);

  // ============================
  // Handlers
  // ============================
  const sendMessage = () => {
    if (!message.trim() || !socket) return;

    socket.emit("group:message", {
      groupId: group._id,
      content: message, // Now contains \n characters
      type: "text",
    });

    socket.emit("group:typing", {
      groupId: group._id,
      isTyping: false,
    });

    setMessage("");
  };

  const handleInputTyping = (e) => {
    const value = e.target.value;
    setMessage(value);

    if (!socket || !group?._id) return;

    socket.emit("group:typing", {
      groupId: group._id,
      isTyping: value.length > 0,
    });
  };

  // FIX: Handles Enter to send and Shift+Enter for new lines
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevents a new line from being added after sending
      sendMessage();
    }
  };

  return (
    <div className="group-chat">
      {/* Header */}
      <div className="group-chat-header">
        <h3>{group.name}</h3>
        <span>{group.participants?.length || 0} members</span>
      </div>

      {/* Messages */}
      <div className="group-chat-body" ref={chatContainerRef}>
        {messages.map((msg) => (
          <div
            key={msg._id}
            className={`group-msg ${msg.sender?._id === user._id ? "own" : ""}`}
          >
            <div className="msg-header">
              <small className="group-msg-user">
                {msg.sender?.username || "Unknown"}
              </small>

              {/* 🟢 The Copy Button */}
              <CopyButton text={msg.content || msg.text} />
            </div>

            <div className="group-msg-content">
              {parseMessage(msg.content || msg.text).map((part) =>
                part.type === "link" ? (
                  <a
                    key={part.key}
                    href={part.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#1a73e8", textDecoration: "underline" }}
                  >
                    {part.content}
                  </a>
                ) : (
                  <span key={part.key}>{part.content}</span>
                ),
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      {Object.keys(typingUsers).length > 0 && (
        <div className="group-typing-indicator">
          {Object.values(typingUsers).join(", ")} typing...
        </div>
      )}

      {/* Input Area */}
      <div className="group-chat-input">
        {/* FIX: Changed <input> to <textarea> */}
        <textarea
          value={message}
          onChange={handleInputTyping}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows="1"
        />
        <button onClick={sendMessage} disabled={!message.trim()}>
          Send
        </button>
      </div>
    </div>
  );
};

export default GroupChat;
